/**
 * Zod schemas for EDU Component Catalog validated types.
 *
 * This is the single source of truth — TypeScript types in types.ts are
 * derived from these schemas via z.infer<>.  The validate-edu-metadata
 * script imports the schemas directly for runtime validation.
 */
import { z } from 'zod';

import { EVENT_MOMENT_KINDS, resolveEventKind } from './resolveEventKind';

export const COMPONENT_CATEGORIES = [
  'layout',
  'navigation',
  'content',
  'form',
  'data',
  'subcomponent',
] as const;

/**
 * Canonical CExp Blocks palette job-filter groups (excluding "All").
 * Authors opt components into these via optional `parameters.edu.paletteGroups`.
 * Absent metadata means the component appears under All only.
 */
export const PALETTE_JOB_GROUP_IDS = [
  'entry',
  'results',
  'layout',
  'composites',
] as const;

/**
 * Lucide icon slug for the CExp Blocks palette (kebab-case, e.g. `layout-grid`).
 * Optional — admin falls back to a category default when omitted.
 */
export const PaletteIconSchema = z
  .string()
  .regex(/^[a-z][a-z0-9]*(-[a-z0-9]+)*$/, {
    message:
      'paletteIcon must be a Lucide kebab-case slug (e.g. layout-grid, mouse-pointer-click)',
  });

export const COMPONENT_ID_PATTERN = /^hen\.[a-z][a-zA-Z]*$/;
export const COMPOUND_ID_PATTERN = /^hen\.[a-z][a-zA-Z]*(\.[a-z][a-zA-Z]*)+$/;

export const TITLE_PREFIXES = ['Atomics', 'Composites', 'Layout'] as const;

export const SLOT_TYPES = ['slot', 'string'] as const;

export const PaletteGroupsSchema = z
  .array(z.enum(PALETTE_JOB_GROUP_IDS))
  .nonempty()
  .refine((groups) => new Set(groups).size === groups.length, {
    message: 'paletteGroups must not contain duplicate values',
  });

export const InlineSubComponentSchema = z.object({
  id: z.string().regex(COMPOUND_ID_PATTERN),
  exportName: z.string().trim().min(1),
  displayName: z.string().trim().min(1),
  config: z
    .object({
      allowedComponents: z.array(z.string()).optional(),
    })
    .optional(),
});

export const CatalogAllowedComponentSchema = z.union([
  z.string(),
  InlineSubComponentSchema,
]);

export const CatalogSlotDefinitionSchema = z.object({
  label: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1),
  type: z.enum(SLOT_TYPES).optional(),
  allowedComponents: z.array(CatalogAllowedComponentSchema).optional(),
  /** Catalog ids the editor must not drop into this slot. */
  disallowedComponents: z
    .array(z.string().regex(COMPONENT_ID_PATTERN))
    .optional(),
  /** Render Puck slot children directly when a wrapper changes host behavior. */
  passthrough: z.boolean().optional(),
  /**
   * Render this component as Puck's slot host so the drop-target ref and
   * empty min-height land on the component, and slot children stay direct.
   */
  host: z.boolean().optional(),
});

const CatalogSlotsSchema = z
  .record(CatalogSlotDefinitionSchema)
  .superRefine((slots, ctx) => {
    for (const [slotName, slot] of Object.entries(slots)) {
      if (slot.host !== undefined && slotName !== 'children') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [slotName, 'host'],
          message: `slots.${slotName}.host is only supported on the children slot`,
        });
      }
    }
  });

export const CatalogEventDefinitionSchema = z
  .object({
    description: z.string().trim().min(1),
    kind: z.enum(EVENT_MOMENT_KINDS).optional(),
    eventName: z.string().trim().min(1).optional(),
    payload: z.record(z.string()).optional(),
  })
  .superRefine((entry, context) => {
    const resolved = resolveEventKind(entry);
    // `=== false` narrows this union where strictNullChecks is off.
    if (resolved.ok === false) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['kind'],
        message: resolved.message,
      });
    }
  });

export const EduStoryArgTypesSchema = z.record(z.unknown());

export const SingleEduStoryParametersSchema = z.object({
  id: z.string().regex(COMPONENT_ID_PATTERN),
  category: z.enum(COMPONENT_CATEGORIES),
  slots: CatalogSlotsSchema.optional(),
  events: z.record(CatalogEventDefinitionSchema).optional(),
  paletteGroups: PaletteGroupsSchema.optional(),
  paletteIcon: PaletteIconSchema.optional(),
  requiresDataRepeater: z.boolean().optional(),
  /**
   * When true, the catalog runtime reconstructs slot children as real
   * component elements so hosts that read their children can see each item.
   * Accordion and other mount-the-slot hosts omit this.
   */
  resolveSlotChildren: z.boolean().optional(),
});

export const EduComponentEntrySchema = z.object({
  exportName: z.string().trim().min(1),
  id: z
    .string()
    .regex(
      new RegExp(
        `${COMPONENT_ID_PATTERN.source}|${COMPOUND_ID_PATTERN.source}`,
      ),
    ),
  /** Block name in the editor palette; defaults to the humanized export name. */
  displayName: z.string().trim().min(1).optional(),
  category: z.enum(COMPONENT_CATEGORIES),
  argTypesInherit: z.boolean().optional(),
  argTypes: EduStoryArgTypesSchema.optional(),
  slots: CatalogSlotsSchema.optional(),
  events: z.record(CatalogEventDefinitionSchema).optional(),
  paletteGroups: PaletteGroupsSchema.optional(),
  paletteIcon: PaletteIconSchema.optional(),
  requiresDataRepeater: z.boolean().optional(),
  resolveSlotChildren: z.boolean().optional(),
});

export const MultiEduStoryParametersSchema = z
  .array(EduComponentEntrySchema)
  .min(1);

export const EduStoryParametersSchema = z.union([
  SingleEduStoryParametersSchema,
  MultiEduStoryParametersSchema,
]);

export type ComponentCategory = (typeof COMPONENT_CATEGORIES)[number];
export type PaletteJobGroupId = (typeof PALETTE_JOB_GROUP_IDS)[number];
export type CatalogAllowedComponent = z.infer<
  typeof CatalogAllowedComponentSchema
>;
export type InlineSubComponent = z.infer<typeof InlineSubComponentSchema>;
export type CatalogSlotDefinition = z.infer<typeof CatalogSlotDefinitionSchema>;
export type CatalogEventDefinition = z.infer<
  typeof CatalogEventDefinitionSchema
>;
export type EduStoryArgTypes = z.infer<typeof EduStoryArgTypesSchema>;
export type SingleEduStoryParameters = z.infer<
  typeof SingleEduStoryParametersSchema
>;
export type EduComponentEntry = z.infer<typeof EduComponentEntrySchema>;
export type MultiEduStoryParameters = z.infer<
  typeof MultiEduStoryParametersSchema
>;
export type EduStoryParameters = z.infer<typeof EduStoryParametersSchema>;
